import { BookOpen, CheckCircle2, ImageIcon, LifeBuoy, List, Printer, Search, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { filterGuideSections, GuideCallout, GuideSection } from './helpDeskGuideContent';

function GuideImage({ image, imageAlt, caption, callouts = [] }: { image: string; imageAlt: string; caption: string; callouts?: GuideCallout[] }) {
  return (
    <figure className="guide-avoid-break rounded-3xl border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-600">
        <ImageIcon className="h-4 w-4" /> {caption}
      </div>
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <img src={image} alt={imageAlt} className="w-full object-cover" loading="lazy" />
        {callouts.map((callout) => (
          <div
            key={`${callout.title}-${callout.x}-${callout.y}`}
            className="absolute max-w-[220px]"
            style={{ left: `${callout.x}%`, top: `${callout.y}%`, transform: 'translate(-50%, -50%)' }}
          >
            <div className="flex items-start gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-cyan-600 text-xs font-bold text-white shadow-lg ring-4 ring-white">
                {callout.title}
              </div>
              <div className="rounded-2xl border border-cyan-200 bg-white/95 px-3 py-2 text-[11px] leading-5 text-slate-700 shadow-lg backdrop-blur print:bg-white">
                {callout.text}
              </div>
            </div>
          </div>
        ))}
      </div>
    </figure>
  );
}

function SectionCard({ section }: { section: GuideSection }) {
  const Icon = section.icon;

  return (
    <section id={section.id} className="guide-avoid-break rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm print:shadow-none">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-700 print:bg-slate-100 print:text-slate-700">
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700 print:text-slate-500">Bolum {section.number}</div>
          <h2 className="mt-1 text-2xl font-bold text-slate-900">{section.title}</h2>
          <p className="mt-3 text-sm leading-6 text-slate-600">{section.summary}</p>
        </div>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl bg-slate-50 p-4">
          <div className="text-sm font-semibold text-slate-900">Hedef Kitle</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {section.audience.map((item) => (
              <span key={item} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700">{item}</span>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-slate-50 p-4">
          <div className="text-sm font-semibold text-slate-900">Erisim Yollari</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {section.paths.map((item) => (
              <code key={item} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700">{item}</code>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 p-4">
          <div className="text-sm font-semibold text-slate-900">Kapsanan Moduller</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {section.modules.map((item) => (
              <span key={item} className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-800 print:bg-slate-100 print:text-slate-800">{item}</span>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 p-4">
          <div className="text-sm font-semibold text-slate-900">Tipik Kullanici Gorevleri</div>
          <div className="mt-3 space-y-2 text-sm text-slate-700">
            {section.tasks.map((item) => (
              <div key={item} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
        <div className="font-semibold">Help Desk Notlari</div>
        <div className="mt-3 space-y-2">
          {section.support.map((item) => (
            <div key={item}>{item}</div>
          ))}
        </div>
      </div>

      {section.visuals?.length ? (
        <div className="mt-5 grid gap-4 xl:grid-cols-2">
          {section.visuals.map((visual) => (
            <GuideImage
              key={`${section.id}-${visual.image}`}
              image={visual.image}
              imageAlt={visual.imageAlt}
              caption={visual.caption}
              callouts={visual.callouts}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function HelpDeskGuide() {
  const [query, setQuery] = useState('');
  const sections = useMemo(() => filterGuideSections(query), [query]);

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-8 print:bg-white print:p-0">
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 14mm;
          }

          .guide-print-hide {
            display: none !important;
          }

          .guide-avoid-break {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>

      <div className="grid w-full gap-8 xl:grid-cols-[290px_minmax(0,1fr)]">
        <aside className="guide-print-hide xl:sticky xl:top-24 xl:self-start">
          <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">Icerik Menusu</div>
                <h2 className="mt-1 text-lg font-bold text-slate-900">Tum Sistem Rehberi</h2>
              </div>
              <List className="h-5 w-5 text-slate-400" />
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ekran, modul veya yol ara"
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-cyan-400"
                />
              </div>
              <div className="mt-2 text-xs text-slate-500">{sections.length} bolum listeleniyor</div>
            </div>

            <div className="mt-5 space-y-2 max-h-[52vh] overflow-auto pr-1">
              {sections.map((section) => (
                <a key={section.id} href={`#${section.id}`} className="block rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 transition-colors hover:bg-slate-50">
                  <div className="font-semibold text-slate-900">{section.number}. {section.title}</div>
                  <div className="mt-1 text-xs text-slate-500">{section.modules.slice(0, 3).join(' • ')}</div>
                </a>
              ))}
            </div>

            <div className="mt-5 grid gap-3">
              <Link to="/help-desk-guide-export" target="_blank" className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <Printer className="h-4 w-4" /> PDF Export Gorunumu
              </Link>
            </div>
          </div>
        </aside>

        <main className="space-y-8">
          <section className="guide-avoid-break overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm print:shadow-none">
            <div className="bg-gradient-to-r from-cyan-700 via-sky-700 to-indigo-700 px-8 py-10 text-white print:bg-slate-900">
              <div className="flex flex-wrap items-center gap-3 text-sm font-medium text-white/80">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5"><LifeBuoy className="h-4 w-4" /> Help Desk</span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5"><BookOpen className="h-4 w-4" /> Kurumsal Kullanim Kilavuzu</span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5"><Users className="h-4 w-4" /> Daha Fazla Pencere ve Moduller</span>
              </div>

              <h1 className="mt-5 text-3xl font-bold tracking-tight">Yuk Ortagim Kurumsal Kullanim Kilavuzu</h1>
              <p className="mt-3 max-w-4xl text-sm leading-6 text-cyan-50">
                Bu surum daha fazla pencereyi galeri halinde kapsar. Rehber icinde arama yapabilir, bolum bazli ilerleyebilir ve ayni icerigi PDF export gorunumuyle ayri sekmede acabilirsiniz.
              </p>
            </div>

            <div className="grid gap-4 border-t border-slate-100 px-8 py-6 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-sm font-semibold text-slate-900">Kapsam</div>
                <p className="mt-2 text-sm text-slate-600">Tum ana menu gruplari, mobil surucu akisi, destek ve teknik yonetim modulleri.</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-sm font-semibold text-slate-900">Arama</div>
                <p className="mt-2 text-sm text-slate-600">Modul, ekran adi, yol veya destek notuna gore filtreleme yapabilirsiniz.</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-sm font-semibold text-slate-900">Export</div>
                <p className="mt-2 text-sm text-slate-600">Ayni icerik icin ayri <code>PDF Export</code> gorunumu hazirdir.</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-sm font-semibold text-slate-900">Gorseller</div>
                <p className="mt-2 text-sm text-slate-600">Her ana modul icin ek pencere goruntuleri galeri seklinde gosterilir.</p>
              </div>
            </div>
          </section>

          {sections.length === 0 ? (
            <section className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
              <h2 className="text-xl font-bold text-slate-900">Eslesen bolum bulunamadi</h2>
              <p className="mt-2 text-sm text-slate-600">Farkli bir ekran adi, modul ismi veya URL yolu ile tekrar arayin.</p>
            </section>
          ) : (
            sections.map((section) => <SectionCard key={section.id} section={section} />)
          )}
        </main>
      </div>
    </div>
  );
}
